<script lang="ts" setup>
import type { ComputedRef } from 'vue'

import truncate from 'lodash-es/truncate'

import { BODY } from '/@/constants/common'

interface Props {
  /**
   * @var value {string}
   * @description Holds text value.
   */
  value: string
  /**
   * @var textLength {number}
   * @description Holds text length.
   */
  textLength?: number
  /**
   * @var isInteractive {boolean}
   * @description Determines if the text is interactive.
   */
  isInteractive?: boolean
  /**
   * @var disableAutoTruncate {boolean}
   * @description Overrides default text length restrictions.
   */
  disableAutoTruncate?: boolean
  /**
   * @name longText
   * @description If the text is long, it will be larger than default width.
   */
  longText?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  textLength: 20,
  isInteractive: true,
})

const tippyProps: ComputedRef<Recordable> = computed(() => ({
  interactive: props.isInteractive,
  appendTo: BODY,
  theme: props.longText ? 'default-larger' : 'default',
}))

function truncateText(text: string): string {
  if (props.disableAutoTruncate) return text
  return text?.length > props.textLength
    ? truncate(text, { length: props.textLength, omission: '...' })
    : text
}
</script>

<template>
  <Tippy v-bind="tippyProps" max-width="100%" class="wrapper">
    {{ truncateText(value) }}
    <template v-if="value?.length > textLength && !disableAutoTruncate" #content>
      <div class="content">
        {{ value }}
      </div>
    </template>
  </Tippy>
</template>

<style lang="scss" scoped>
.content {
  @apply whitespace-pre-wrap break-words;
}
</style>
